import React, { useState, useEffect } from 'react';
import { LogOut, QrCode, List, BarChart3, ShoppingBag, CheckCircle, Clock, XCircle } from 'lucide-react';
import axios from 'axios';
import QRScanner from './QRScanner';
import VoucherList from './VoucherList';
import ManualEntry from './ManualEntry';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BusinessDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('scan');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('business_token');
      const response = await axios.get(`${API}/api/business/vouchers/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('business_token');
    localStorage.removeItem('business_user');
    onLogout();
  };

  return (
    <div className="min-h-screen bg-[#E8E2D2]">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#8B3A3A] to-[#6B2A2A] text-[#F5F0E1] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold">{user.business_name}</h1>
              <p className="font-utility text-sm opacity-90">{user.contact_name}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors font-utility text-sm"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      {!loading && stats && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<ShoppingBag />}
              title="Total Vouchers"
              value={stats.total_vouchers}
              color="blue"
            />
            <StatCard
              icon={<Clock />}
              title="Active"
              value={stats.active_vouchers}
              color="green"
            />
            <StatCard
              icon={<CheckCircle />}
              title="Redeemed"
              value={stats.redeemed_vouchers}
              color="purple"
            />
            <StatCard
              icon={<BarChart3 />}
              title="Redemption Rate"
              value={`${stats.redemption_rate}%`}
              color="orange"
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="border-b border-[#2C2416]/10">
            <div className="flex">
              <TabButton
                active={activeTab === 'scan'}
                onClick={() => setActiveTab('scan')}
                icon={<QrCode size={18} />}
                label="Scan QR Code"
              />
              <TabButton
                active={activeTab === 'manual'}
                onClick={() => setActiveTab('manual')}
                icon={<QrCode size={18} />}
                label="Enter Code"
              />
              <TabButton
                active={activeTab === 'history'}
                onClick={() => setActiveTab('history')}
                icon={<List size={18} />}
                label="History"
              />
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'scan' && <QRScanner onScan={(code) => setActiveTab('manual')} />}
            {activeTab === 'manual' && <ManualEntry onRedeemSuccess={fetchStats} />}
            {activeTab === 'history' && <VoucherList />}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, color }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600'
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${colors[color]} text-white mb-3`}>
        {icon}
      </div>
      <p className="font-utility text-sm text-[#2C2416]/60 mb-1">{title}</p>
      <p className="font-heading text-3xl font-bold text-[#2C2416]">{value}</p>
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-6 py-3 font-utility font-medium transition-colors border-b-2 ${
      active
        ? 'border-[#8B3A3A] text-[#8B3A3A]'
        : 'border-transparent text-[#2C2416]/50 hover:text-[#2C2416]'
    }`}
  >
    {icon}
    {label}
  </button>
);

export default BusinessDashboard;
